import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	Output,
	SimpleChanges
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import type { MarkerSet, Marker } from '@go-modules/interfaces/markers/marker';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { MarkerDisplayModule } from '@go-modules/components/marker-display/marker-display.module';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { MarkerSetDialogComponent } from '@go-modules/components/dialogs/marker-set-dialog/marker-set-dialog.component';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { tagSetModelToken } from 'go-modules/models/tag-set/tag-set.factory';
import { tagModelToken } from 'go-modules/models/tag/tag.factory';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';

interface LegacyMarkerSet extends MarkerSet {
	$promise?: Promise<MarkerSet>;
}

interface TagSetModelNg1 {
	new (config: Partial<MarkerSet>): LegacyMarkerSet;
	canUpdate(params: { id: number }): { $promise: Promise<{ can_update: boolean }> };
}

type TagModelNg1 = new (config: Partial<Marker>) => Marker;

const enum MarkerSetModalMode {
	EDIT_EXISTING = 'edit-existing',
	EDIT_COPY = 'edit-copy'
}

export function isLegacyMarkerSet (value: unknown): value is LegacyMarkerSet {
	return Boolean((value as LegacyMarkerSet)?.$promise);
}

export function isPromiseLike<T> (value: unknown): value is Promise<T> {
	return typeof (value as Promise<T>)?.then === 'function';
}

@Component({
	selector: 'marker-set-panel',
	standalone: true,
	imports: [
		TranslateModule,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		MarkerDisplayModule
	],
	templateUrl: './marker-set-panel.component.html',
	styleUrls: ['./marker-set-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MarkerSetPanelComponent implements OnChanges {
	@Input() public markerSet: LegacyMarkerSet | Promise<MarkerSet> | null | undefined;
	@Input() public activity: ActivityModel | null | undefined;
	@Output() public onEdited: EventEmitter<{ markerSet: MarkerSet }> = new EventEmitter();
	@Output() public onRemoved: EventEmitter<void> = new EventEmitter();

	public isLoading: boolean = false;
	public isFetchingEditPermissions: boolean = false;
	public resolvedMarkerSet: MarkerSet | null = null;
	public markers: Marker[] = [];
	public readonly toolbarActions = Action;

	constructor (
		private readonly cdr: ChangeDetectorRef,
		private readonly translate: TranslateService,
		private readonly goModalService: GoModalService,
		private readonly ngxGoToastService: NgxGoToastService,
		private readonly dialog: MatDialog,
		@Inject(tagSetModelToken) private readonly tagSetModel: TagSetModelNg1,
		@Inject(tagModelToken) private readonly tagModel: TagModelNg1
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.markerSet) {
			this.loadMarkerSet().catch(() => {});
		}
	}

	public async handleEdit (): Promise<void> {
		if (!this.resolvedMarkerSet?.id) {
			return;
		}

		this.isFetchingEditPermissions = true;
		this.cdr.markForCheck();

		try {
			const response = await this.tagSetModel
				.canUpdate({ id: this.resolvedMarkerSet.id }).$promise;

			if (response?.can_update) {
				await this.openMarkerSetModal(MarkerSetModalMode.EDIT_EXISTING);
			} else {
				await this.openConfirmCopyFlow();
			}
		} catch {
			this.ngxGoToastService.createToast({
				type: GoToastStatusType.ERROR,
				message: 'marker-modifications-permission-error'
			});
		} finally {
			this.isFetchingEditPermissions = false;
			this.cdr.markForCheck();
		}
	}

	public async handleRemove (): Promise<void> {
		if (!this.resolvedMarkerSet) {
			return;
		}

		const isDefaultAccount = this.activity?.name === '__ACCT_DEFAULT__';
		const title = this.resolvedMarkerSet.title ?? '';

		const dialogRef = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('modal-remove-marker-set_remove-markers'),
				message: isDefaultAccount ?
					this.translate.instant('modal-remove-marker-set_sure-remove-default', { name: title }) :
					this.translate.instant('modal-remove-marker-set_sure-remove', { name: title }),
				note: this.translate.instant('modal-remove-marker-set_note'),
				confirmText: this.translate.instant('common_remove'),
				cancelText: this.translate.instant('common_cancel')
			}
		});

		const confirmed = await firstValueFrom(dialogRef.afterClosed());
		if (confirmed) {
			this.onRemoved.emit();
		}
	}

	private async loadMarkerSet (): Promise<void> {
		if (!this.markerSet) {
			this.resolvedMarkerSet = null;
			this.markers = [];
			this.cdr.markForCheck();
			return;
		}

		this.isLoading = true;
		this.cdr.markForCheck();

		try {
			const resolved = await this.awaitMarkerSetResolution(this.markerSet);
			this.resolvedMarkerSet = resolved ?? null;
			this.sortResolvedMarkerTags();
		} finally {
			this.isLoading = false;
			this.cdr.markForCheck();
		}
	}

	private async awaitMarkerSetResolution (
		markerSet: LegacyMarkerSet | Promise<MarkerSet>
	): Promise<MarkerSet> {
		if (isLegacyMarkerSet(markerSet) && markerSet.$promise) {
			const result = await markerSet.$promise;
			return result ?? markerSet;
		}

		if (isPromiseLike<MarkerSet>(markerSet)) {
			return await markerSet;
		}

		return markerSet as MarkerSet;
	}

	private async openMarkerSetModal (mode: MarkerSetModalMode): Promise<void> {
		if (!this.resolvedMarkerSet) {
			return;
		}

		const modalData = mode === MarkerSetModalMode.EDIT_EXISTING
			? { markerSet: this.resolvedMarkerSet }
			: {
				markerSet: this.buildMarkerSetCopy(),
				copying: true
			};

		const dialogRef = this.goModalService.open(MarkerSetDialogComponent, false, {
			data: modalData
		});

		const result = await firstValueFrom(dialogRef.afterClosed());

		if (!result || result.dismissed) {
			return;
		}

		this.markerSet = result;
		this.resolvedMarkerSet = result;
		this.sortResolvedMarkerTags();
		this.onEdited.emit({ markerSet: this.resolvedMarkerSet });
		this.cdr.markForCheck();
	}

	private buildMarkerSetCopy (): MarkerSet {
		const tags = this.markers.map((tag) => new this.tagModel({
			tag_abbreviation: tag.tag_abbreviation,
			tag_color: tag.tag_color,
			tag_name: tag.tag_name
		}));

		const title = this.translate.instant('common_copy-of', {
			title: this.resolvedMarkerSet?.title ?? ''
		});

		return new this.tagSetModel({
			tags,
			title
		});
	}

	private async openConfirmCopyFlow (): Promise<void> {
		const dialogRef = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('marker-modifications-disabled'),
				message: this.translate.instant('marker-modifications-disabled-description'),
				confirmText: this.translate.instant('common_create-copy'),
				cancelText: this.translate.instant('common_cancel')
			}
		});

		const confirmed = await firstValueFrom(dialogRef.afterClosed());
		if (confirmed) {
			await this.openMarkerSetModal(MarkerSetModalMode.EDIT_COPY);
		}
	}

	private sortResolvedMarkerTags (): void {
		if (!this.resolvedMarkerSet?.tags) {
			this.markers = [];
			return;
		}

		this.resolvedMarkerSet.tags.sort((a, b) => {
			const sortA = a.sort_index ?? 0;
			const sortB = b.sort_index ?? 0;
			return sortA - sortB;
		});

		this.markers = [...this.resolvedMarkerSet.tags];
	}
}
